<template>
  <!-- 这是一个无UI的监控组件 -->
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount } from 'vue';
import { usePagePerformance } from '@/composables/usePagePerformance';
import { useAPITracking } from '@/composables/useAPITracking';
import { 
  getPerformanceMetrics, 
  observeLCP, 
  observeFCP,
  observeFP,
  observeCLS,
  observeFID, 
  observeWebVitals,
  waitForDOMContentLoaded,
  waitForPageLoad,
  createAPIPerformanceInterceptor
} from '@/utils/performance';
import { addAPITracking } from '@/utils/http';

interface Props {
  // 是否启用API性能监控
  enableAPIMonitoring?: boolean;
  // 是否启用Web Vitals监控
  enableWebVitals?: boolean;
  // 是否启用API埋点统计
  enableAPITracking?: boolean;
  // 容器类型
  containerType?: 'H5' | 'FinClip' | 'Android' | 'iOS' | 'Flutter';
  // 自定义trigger_ts
  customTriggerTs?: string;
}

const props = withDefaults(defineProps<Props>(), {
  enableAPIMonitoring: true,
  enableWebVitals: true,
  enableAPITracking: true,
  containerType: 'H5',
  customTriggerTs: ''
});

const {
  initPerformanceMonitoring,
  recordPageCreate,
  recordMainRequestStart,
  recordMainRequestEnd,
  recordPageReady,
  recordPageInteractive,
  recordFirstContentfulPaint,
  recordLargestContentfulPaint,
  setContainerType,
  sendPerformanceData,
  performanceTiming
} = usePagePerformance();

// API埋点统计
const { createFetchInterceptor } = useAPITracking();

// 清理函数
let cleanupFunctions: Array<() => void> = [];

onMounted(async () => {
  try {
    // 1. 初始化性能监控
    initPerformanceMonitoring();
    
    // 2. 设置容器类型
    setContainerType(props.containerType);
    
    // 3. 记录页面创建时间
    recordPageCreate();
    
    // 4. 启用API性能监控
    if (props.enableAPIMonitoring) {
      createAPIPerformanceInterceptor();
    }
    
    // 4.1. 启用API埋点统计
    if (props.enableAPITracking) {
      // 为axios实例添加埋点拦截器
      addAPITracking();
      // 为fetch添加埋点拦截器
      createFetchInterceptor();
    }
    
    // 5. 记录主要请求开始时间
    recordMainRequestStart();
    
    // 6. 监听DOM内容加载完成
    await waitForDOMContentLoaded();
    recordPageReady();
    
    // 7. 监听页面完全加载
    await waitForPageLoad();
    recordMainRequestEnd();
    
    // 8. 启用Web Vitals监控
    if (props.enableWebVitals) {
      // 使用统一的Web Vitals监听函数
      const webVitalsCleanup = observeWebVitals({
        onFP: (fp) => {
          // console.log('FP:', fp);
          // FP时间记录已移除，使用FCP替代
        },
        onFCP: (fcp) => {
          // console.log('FCP:', fcp);
          recordFirstContentfulPaint(fcp);
        },
        onLCP: (lcp) => {
          // console.log('LCP:', lcp);
          recordLargestContentfulPaint(lcp);
          // LCP 收集完成后，直接上报性能数据
          // console.log('✅ LCP收集完成，立即上报性能数据');
          sendPerformanceData();
        },
        onCLS: (cls) => {
          // console.log('CLS:', cls);
          // CLS记录已移除
        },
        onFID: (fid) => {
          // console.log('FID:', fid);
          recordPageInteractive();
        }
      });
      cleanupFunctions.push(webVitalsCleanup);
    }
    
    // 9. 延迟记录页面可交互时间
    setTimeout(() => {
      recordPageInteractive();
    }, 100);
    
    // 10. 如果 Web Vitals 监控被禁用，则使用备用策略
    if (!props.enableWebVitals) {
      setTimeout(() => {
        sendPerformanceData();
      }, 3000); // 3秒后上报，作为备用策略
    }
    
    console.log('页面性能监控已启动');
    
  } catch (error) {
    console.error('页面性能监控启动失败:', error);
  }
});

onBeforeUnmount(() => {
  // 清理所有监听器
  cleanupFunctions.forEach(cleanup => cleanup());
  cleanupFunctions = [];
  
  console.log('页面性能监控已清理');
});
</script>
